import { z } from 'zod'

import {
  recordGroupSchema,
  recordGroupDetailSchema,
  recordGroupListPageSchema,
  recordGroupSummarySchema,
  createRecordGroupRequestSchema,
  trashedRecordGroupItemSchema,
  trashedRecordGroupPageSchema,
  type RecordGroup,
  type RecordGroupDetail,
  type RecordGroupListPage,
  type RecordGroupSearchParams,
  type RecordGroupSummary,
  type CreateRecordGroupRequest,
  type TrashedRecordGroupItem,
  type TrashedRecordGroupPage,
} from '@/types/api/record-group'
import { trashStatsSchema, cleanupResultSchema, type TrashStats, type CleanupResult } from '@/types/api/trash'

import { apiClient } from './client'


export async function getRecordGroups(): Promise<RecordGroup[]> {
  const response = await apiClient.get('/record-groups')
  return z.array(recordGroupSchema).parse(response.data)
}

/** One group with its breadcrumb trail (404 unless viewable or traceable). */
export async function getRecordGroup(id: string): Promise<RecordGroupDetail> {
  const response = await apiClient.get(`/record-groups/${id}`)
  return recordGroupDetailSchema.parse(response.data)
}

export async function searchRecordGroups(params: RecordGroupSearchParams): Promise<RecordGroupListPage> {
  const response = await apiClient.get('/record-groups/search', {
    params: { ...params, q: params.q?.trim() || undefined },
  })
  return recordGroupListPageSchema.parse(response.data)
}

export async function getRecordGroupSummary(parentId?: string): Promise<RecordGroupSummary> {
  const response = await apiClient.get('/record-groups/summary', { params: { parentId } })
  return recordGroupSummarySchema.parse(response.data)
}

export async function createRecordGroup(data: CreateRecordGroupRequest): Promise<RecordGroup> {
  const validated = createRecordGroupRequestSchema.parse(data)
  const response = await apiClient.post('/record-groups', validated)
  return recordGroupSchema.parse(response.data)
}

export async function updateRecordGroup(id: string, data: CreateRecordGroupRequest): Promise<RecordGroup> {
  const validated = createRecordGroupRequestSchema.parse(data)
  const response = await apiClient.put(`/record-groups/${id}`, validated)
  return recordGroupSchema.parse(response.data)
}

/** Set (tagId) or clear (null) the record tag applied to every new record of the group. */
export async function setDefaultRecordTag(id: string, tagId: string | null): Promise<RecordGroup> {
  const response = await apiClient.put(`/record-groups/${id}/default-record-tag`, { tagId })
  return recordGroupSchema.parse(response.data)
}

export async function deleteRecordGroup(id: string): Promise<void> {
  await apiClient.delete(`/record-groups/${id}`)
}

export async function getTrashedRecordGroups(pageNumber: number, pageSize: number): Promise<TrashedRecordGroupPage> {
  const response = await apiClient.get(`/record-groups/trash`, {
    params: { page: pageNumber, size: pageSize },
  })
  return trashedRecordGroupPageSchema.parse(response.data)
}

export async function getAllTrashedRecordGroups(): Promise<TrashedRecordGroupItem[]> {
  const response = await apiClient.get('/record-groups/trash/all')
  return z.array(trashedRecordGroupItemSchema).parse(response.data)
}

export async function searchTrashedRecordGroups(query: string, pageNumber: number, pageSize: number): Promise<TrashedRecordGroupPage> {
  const response = await apiClient.get(`/record-groups/trash/search`, {
    params: { query, pageNumber, pageSize },
  })
  return trashedRecordGroupPageSchema.parse(response.data)
}

export async function getRecordGroupTrashStats(): Promise<TrashStats> {
  const response = await apiClient.get('/record-groups/trash/stats')
  return trashStatsSchema.parse(response.data)
}

export async function restoreRecordGroup(id: string): Promise<TrashedRecordGroupItem> {
  const response = await apiClient.post(`/record-groups/trash/${id}/restore`, undefined, {
    suppressErrorToast: true,
  })
  return trashedRecordGroupItemSchema.parse(response.data)
}

export async function permanentlyDeleteRecordGroup(id: string): Promise<void> {
  await apiClient.delete(`/record-groups/trash/${id}`, {
    suppressErrorToast: true,
  })
}

export async function triggerRecordGroupTrashCleanup(): Promise<CleanupResult> {
  const response = await apiClient.post('/record-groups/trash/cleanup/now')
  return cleanupResultSchema.parse(response.data)
}